import { useAuthStore } from "@/stores/auth";
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios";
import { type ToastInterface, useToast } from "vue-toastification";

class ApiClient {
  private client: AxiosInstance;
  private _toast: ToastInterface | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || "http://localhost:4000",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  // Lazy initialization of toast to avoid calling before Vue app is ready
  private get toast(): ToastInterface {
    if (!this._toast) {
      this._toast = useToast();
    }
    return this._toast;
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore();
        const token = authStore.token;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: unknown) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor - Handle errors globally
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<{ error?: string; required?: string }>) => {
        if (error.response?.status === 401) {
          // Skip toast for /api/auth/me requests (silent token validation)
          const isAuthMeRequest = error.config?.url?.includes('/api/auth/me');

          if (!isAuthMeRequest) {
            // Unauthorized - Show error, don't auto-logout
            const errorMsg = error.response.data?.error || "Authentication required";

            this.toast.error(`Authentifizierung fehlgeschlagen: ${errorMsg}`, {
              timeout: 5000,
            });
          }

          return Promise.reject({ ...error, __handled: true });
        } else if (error.response?.status === 403) {
          // Forbidden - insufficient permissions
          const required = error.response.data?.required;
          const message = required
            ? `Keine Berechtigung: ${required}`
            : "Keine Berechtigung für diese Aktion";

          this.toast.error(message, {
            timeout: 5000,
          });

          return Promise.reject({ ...error, __handled: true });
        }

        return Promise.reject(error);
      },
    );
  }

  async get<T>(url: string, params?: unknown): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async getBlob(url: string, params?: unknown): Promise<Blob> {
    const response = await this.client.get<Blob>(url, {
      params,
      responseType: "blob",
    });
    return response.data;
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, params?: unknown): Promise<T> {
    const response = await this.client.delete<T>(url, { params });
    return response.data;
  }
}

export const api = new ApiClient();
